import React from "react";
import { Building2, Navigation, Loader2, RefreshCw, CheckCircle } from "lucide-react";
import { NearestClinicData } from "./types";

interface NearestClinicCardProps {
  loading: boolean;
  clinic: NearestClinicData | null;
  error?: string | null;
  onRetry?: () => void;
}

export const NearestClinicCard: React.FC<NearestClinicCardProps> = ({
  loading,
  clinic,
  error,
  onRetry,
}) => {
  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-emerald-50/40 border border-emerald-200 flex items-center justify-center gap-2 text-xs font-semibold text-emerald-700 animate-pulse">
        <Loader2 size={16} className="animate-spin text-emerald-600" />
        Calculating nearest clinic for selected address...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-2 text-xs font-medium text-amber-800">
        <span>{error || "Could not determine nearest clinic."}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-2.5 py-1 text-[0.7rem] font-bold text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
          >
            <RefreshCw size={12} /> Retry
          </button>
        )}
      </div>
    );
  }

  if (!clinic) return null;

  return (
    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-200/80 shadow-sm animate-fadeIn">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center shadow-xs">
            <Building2 size={15} />
          </div>
          <div>
            <span className="text-[0.65rem] font-extrabold uppercase text-emerald-700 tracking-wider block">
              Assigned Nearest Clinic
            </span>
            <h4 className="text-xs font-extrabold text-slate-800">
              {clinic.clinicName}
            </h4>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.68rem] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <Navigation size={11} className="text-emerald-600" />
          {clinic.distance !== undefined
            ? `${clinic.distance.toFixed(2)} km away`
            : "Nearby"}
        </span>
      </div>

      {clinic.clinicId && (
        <div className="flex items-center justify-between pt-2 border-t border-emerald-100/60 text-[0.68rem] text-slate-500 font-medium">
          <span>Clinic ID: <strong className="text-slate-700">{clinic.clinicId}</strong></span>
          <span className="text-emerald-600 font-bold flex items-center gap-1">
            <CheckCircle size={11} /> Verified for Dispatch
          </span>
        </div>
      )}
    </div>
  );
};

export default NearestClinicCard;
